import React, {useEffect, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {WrapperWithPadding} from 'components/Accommodation/WrapperWithPadding'
import {FAQItem, FAQs} from 'components/FAQs'
import {FAQPositionViewedTracker} from 'components/FAQs/FAQPositionViewedTracker'
import {
  SkeletonLoaderDesktop,
  SkeletonLoaderMobile
} from 'components/FAQs/SkeletonLoader'
import {ScrollSection} from 'components/ScrollSpy'
import {useGetFaqsAvailibility} from 'hooks/useGetFaqsAvailibility'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {getAccommodationPageHotel} from 'modules/searchApi/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Text} from '@vio/atlas/Text'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

const MobileLayout: React.FC<{children: React.ReactNode}> = ({children}) => (
  <WrapperWithPadding>
    <Box paddingBottom="s700">
      <Divider />
    </Box>
    {children}
  </WrapperWithPadding>
)

const DesktopLayout: React.FC<{children: React.ReactNode}> = ({children}) => (
  <>
    <Box paddingBottom="s900">
      <Divider />
    </Box>
    {children}
    <Box paddingBottom="s900" />
  </>
)

export const ConditionalFAQ = ({
  hotelId,
  scrollTopMargin
}: {
  hotelId: string
  scrollTopMargin?: number
}) => {
  const {shouldRenderFaqs, data, isLoading} = useGetFaqsAvailibility({hotelId})
  const hotel = useSelector(getAccommodationPageHotel)
  const {isMobile} = useDeviceLayout()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})

  const faqs = useMemo(() => data?.faqs ?? [], [data?.faqs])
  const questionsAvailable = useMemo(() => faqs.map(q => q.label), [faqs])

  useEffect(() => {
    if (inView && !isLoading) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsFAQSection,
          action: Action.Viewed,
          payload: {
            questionsAvailable
          }
        })
      )
    }
  }, [dispatchTeamEvent, inView, questionsAvailable, isLoading])

  const handleFAQToggle = (faq: FAQItem, isOpen: boolean) => {
    if (isOpen) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsFAQItem,
          action: Action.Clicked,
          payload: {
            selectedElement: faq.label,
            questionsAvailable
          }
        })
      )
    }
  }

  if (!hotel) return null

  const Layout = isMobile ? MobileLayout : DesktopLayout

  return (
    <>
      {/* This should always be rendered to track the FAQ position viewed*/}
      <FAQPositionViewedTracker />
      {shouldRenderFaqs && (
        <ScrollSection
          name={AccommodationPageNavSections.FAQs}
          scrollTopMargin={scrollTopMargin}
        >
          <Layout>
            {isLoading ? (
              isMobile ? (
                <SkeletonLoaderMobile />
              ) : (
                <SkeletonLoaderDesktop />
              )
            ) : (
              <div ref={ref} data-id="faq-section">
                <Box paddingBottom="s500">
                  <Text variant={isMobile ? 'titleL' : 'titleXL'} tag="h2">
                    <FormattedMessageWrapper
                      id="faqs.titleWithHotelName"
                      defaultMessage="FAQs about {hotelName}"
                      values={{hotelName: hotel.hotelName}}
                    />
                  </Text>
                </Box>
                <FAQs
                  faqs={faqs}
                  isMobile={isMobile}
                  icon="DoubleChatBubble"
                  onToggle={handleFAQToggle}
                />
              </div>
            )}
          </Layout>
        </ScrollSection>
      )}
    </>
  )
}
